/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Container, Box, Tabs, Tab, Chip, CircularProgress } from "@mui/material";
import axios from "../../axios";
import { useTranslation } from "react-i18next";
import styles from "./Help.module.scss";

const TYPE_COLORS = {
  new: { bg: "#e3f0ff", fg: "#1565c0" },
  improved: { bg: "#fff4e0", fg: "#b26a00" },
  fixed: { bg: "#e6f4ea", fg: "#2e7d32" },
};

const Loading = () => (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress sx={{ color: "var(--primary)" }} />
  </Box>
);

const Help = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").toLowerCase().startsWith("nl") ? "nl" : "en";

  const [tab, setTab] = useState(0);
  const [manual, setManual] = useState(null);
  const [changelog, setChangelog] = useState(null);

  useEffect(() => {
    axios
      .get("/exact/store/help/manual", { params: { lang } })
      .then((r) => setManual(r.data.data))
      .catch(() => setManual({ html: "", version: "" }));

    axios
      .get("/exact/store/help/changelog")
      .then((r) => setChangelog(r.data.data))
      .catch(() => setChangelog({ releases: [], version: "" }));
  }, [lang]);

  const version = manual?.version || changelog?.version || window.appVersion || "";
  const typeLabel = (type) =>
    ({ new: t("New"), improved: t("Improved"), fixed: t("Fixed") }[type] || type);

  return (
    <Container sx={{ marginTop: 4, marginBottom: 6 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <h2 style={{ margin: 0 }}>{t("Help")}</h2>
        {version && <Chip label={`v${version}`} size="small" />}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: "var(--primary)" } }}
          sx={{ ".Mui-selected": { color: "var(--primary)" } }}
        >
          <Tab label={t("User Manual")} />
          <Tab label={t("What's New")} />
        </Tabs>
      </Box>

      {tab === 0 &&
        (manual == null ? (
          <Loading />
        ) : (
          <div
            className={styles.manualContent}
            dangerouslySetInnerHTML={{ __html: manual.html }}
          />
        ))}

      {tab === 1 &&
        (changelog == null ? (
          <Loading />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {(changelog.releases || []).map((rel, i) => (
              <Box key={rel.version} sx={{ border: "1px solid #eee", borderRadius: 2, p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <strong style={{ fontSize: "1.1rem" }}>v{rel.version}</strong>
                  {i === 0 && <Chip label={t("Current")} size="small" color="success" />}
                  <span style={{ marginLeft: "auto", color: "#999", fontSize: ".85rem" }}>{rel.date}</span>
                </Box>
                <p style={{ fontWeight: 600, margin: "0 0 .75rem", color: "#444" }}>
                  {rel.title?.[lang] || rel.title?.en}
                </p>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {(rel.items || []).map((item, j) => {
                    const c = TYPE_COLORS[item.type] || { bg: "#eee", fg: "#555" };
                    return (
                      <Box key={j} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                            padding: "2px 7px",
                            borderRadius: 5,
                            background: c.bg,
                            color: c.fg,
                          }}
                        >
                          {typeLabel(item.type)}
                        </span>
                        <span style={{ fontSize: ".9rem", color: "#444" }}>{item[lang] || item.en}</span>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        ))}
    </Container>
  );
};

export default Help;
